import React, { useEffect, useState } from "react";
import {
  Avatar,
  Autocomplete,
  Alert,
  Button,
  Backdrop,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Typography,
  Stack,
  TextField,
  DialogActions, 
  TablePagination,
  Skeleton,
  Snackbar
} from "@mui/material";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import Loader from "../../../../components/Loader";
import { useTheme, styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import WorkIcon from '@mui/icons-material/Work';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';
import ApprovalIcon from '@mui/icons-material/Approval';
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const MAX_DESCRIPTION_LENGTH = 500;

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(2.5),
      overflow: 'hidden',
    },
    '& .MuiDialogContent-root': {
      padding: theme.spacing(2.5),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(2),
    },
}));

async function getContractorWorkProjects() {
    const callable = FirebaseAuth.functions().httpsCallable('getContractorWorkProjects');
    const result = await callable({});
    return Array.isArray(result?.data?.projects) ? result.data.projects : [];
}

const TimesheetSummary = () => {
    const theme = useTheme();
    const today = dayjs();
    const startOfLastMonth = today.subtract(1, "month").startOf("month");
    const endOfCurrentMonth = today.endOf("month");
    const [loading, setLoading] = useState(true);
    const [inSubmit, setInSubmit] = useState(false);
    const [entries, setEntries] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('open');
    const [showDialog, setShowDialog] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [workDate, setWorkDate] = useState(today);
    const [units, setUnits] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [description, setDescription] = useState('');
    const [account, setAccount] = useState('');
    const [dialogPage, setDialogPage] = useState(0);
    const [dialogRowsPerPage, setDialogRowsPerPage] = useState(10);

    useEffect(() => {
        const fetchEntries = async () => {
        const snapshot = await FirebaseAuth.firestore().collection('users').doc(FirebaseAuth.auth().currentUser.uid).collection('timesheets').get();
        const allEntries = [];
        snapshot.forEach((doc) => {
            const data = doc.data();
            (data.entries || []).forEach((entry) => {
            allEntries.push({ ...entry, entryId: String(entry.entry_id || ''), timesheetId: doc.id });
            });
        });
        setEntries(allEntries);
        };

        fetchEntries();
    }, []);

    useEffect(() => {
        const unsubscribe = FirebaseAuth.firestore().collection('users').doc(FirebaseAuth.auth().currentUser.uid).collection('timesheets').onSnapshot((snapshot) => {
        const allEntries = [];
        snapshot.forEach((doc) => {
            const data = doc.data();
            (data.entries || []).forEach((entry) => {
            allEntries.push({ ...entry, entryId: String(entry.entry_id || ''), timesheetId: doc.id });
            });
        });
        setEntries(allEntries);
        });
    
        return () => unsubscribe(); // Cleanup on unmount
    }, []);

    const handleOpen = () => {
        setError(false);
        setMessage('');
        setIsOpen(true);
    }

    const handleClose = () => {
        setIsOpen(false);
    }

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setError(false);
    };

    const shouldDisableDate = (date) => {
        return (
          date.isAfter(today, "day") || // Disable future dates
          date.isBefore(startOfLastMonth, "day") || // Disable dates before last month
          date.isAfter(endOfCurrentMonth, "day") || // Disable dates after current month
          date.day() === 0 || // Disable Sundays
          date.day() === 6 // Disable Saturdays
        );
    };

    const handleUnitChange = (e) => {
        const val = e.target.value;
        
        // Allow empty input or valid decimal
        if (val === '' || /^[0-8](\.\d{0,2})?$/.test(val)) {
            setUnits(val);
        }
    };

    const handleDescriptionChange = (event) => {
        const nextValue = event.target.value || '';
        setDescription(nextValue.slice(0, MAX_DESCRIPTION_LENGTH));
    };

    const submitWorkLog = async (payload) => {
        const submitContractorWorkLog = FirebaseAuth.functions().httpsCallable("submitContractorWorkLog");
        return submitContractorWorkLog(payload);
    };

    const handleCreateEntry = async () => {
        setInSubmit(true);
        try {
            await submitWorkLog({
                action: 'create',
                date: workDate.format('YYYY-MM-DD'),
                account_id: account,
                hours: Number(units),
                description,
            });
            setError(false);
            setAccount('');
            setUnits('');
            setDescription('');
            setMessage('Work log entry submitted for approval');
            handleClose();
            return true;
        } catch (e) {
            setError(true);
            setMessage(e?.message || 'Unable to submit work log entry. Please try again.');
            return false;
        } finally {
            setInSubmit(false);
        }
    }

    const handleStatusClick = (status) => {
        setSelectedStatus(status);
        setDialogPage(0);
    };

    const handleDeleteEntry = async (workRecord) => {
        try {
            await submitWorkLog(workRecord);
            setError(false);
            setMessage('Work log entry removed.');
            return true;
        } catch (e) {
            setError(true);
            setMessage(e?.message || 'Unable to remove work log entry. Please try again.');
            return false;
        }
    }

    const getUserdata = async () => {
        handleOpen();
        setLoading(true);
        try {
            const projects = await getContractorWorkProjects();
            setAccounts(projects);
        } catch (e) {
            setError(true);
            setMessage(e?.message || 'Unable to load projects for work submission. Please try again.');
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }

    const open = entries.filter((entry) => entry.status === 'open');
    const approved = entries.filter((entry) => entry.status === 'approved');

    const totalOpenAmount = open.reduce((sum, e) => sum + (e.hours * e.rate), 0);
    const totalApprovedAmount = approved.reduce((sum, e) => sum + (e.hours * e.rate), 0);

    const entryOpenCount = open.length;
    const entryApprovedCount = approved.length;
    const activeEntries = selectedStatus === 'approved' ? approved : open;
    const activeEntryCount = selectedStatus === 'approved' ? entryApprovedCount : entryOpenCount;
    const activeTotalAmount = selectedStatus === 'approved' ? totalApprovedAmount : totalOpenAmount;
    const activeStatusLabel = selectedStatus === 'approved' ? 'Approved' : 'Open';
    const formatCurrency = (amountInCents) =>
        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((amountInCents || 0) / 100);
    const paginatedEntries = activeEntries.slice(dialogPage * dialogRowsPerPage, dialogPage * dialogRowsPerPage + dialogRowsPerPage);

    return (
        <Paper
            variant="outlined"
            sx={{ p: 0, mb:2, display: 'block', borderRadius: '0px', borderWidth: '0px', backgroundColor: theme.palette.background.paper }}
        >
            <Box sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar variant="rounded" sx={{ height: '32px', width: '32px', bgcolor: theme.palette.grey[700] }}>
                            <WorkIcon fontSize="small" />
                        </Avatar>
                        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                            Work Tracker
                        </Typography>
                    </Stack>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddCircleIcon />}
                        onClick={getUserdata}
                        sx={{ textTransform: 'none', borderRadius: 1.25, px: 1.5, fontWeight: 600 }}
                    >
                        Add work
                    </Button>
                </Stack>
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Paper
                        variant="outlined"
                        onClick={() => handleStatusClick("open")}
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            flex: 1,
                            cursor: 'pointer',
                            borderColor: selectedStatus === 'open' ? 'warning.main' : 'divider',
                            background: selectedStatus === 'open'
                                ? 'linear-gradient(180deg, rgba(255,244,229,0.85), rgba(255,251,245,1))'
                                : 'background.paper',
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>To Be Approved</Typography>
                            <ApprovalIcon fontSize="small" />
                        </Stack>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(totalOpenAmount)}</Typography>
                        <Typography variant="caption" color="text.secondary">{entryOpenCount} entries</Typography>
                    </Paper>

                    <Paper
                        variant="outlined"
                        onClick={() => handleStatusClick("approved")}
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                            flex: 1,
                            cursor: 'pointer',
                            borderColor: selectedStatus === 'approved' ? 'info.main' : 'divider',
                            background: selectedStatus === 'approved'
                                ? 'linear-gradient(180deg, rgba(232,244,253,0.85), rgba(245,250,255,1))'
                                : 'background.paper',
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Pending Payments</Typography>
                            <ReceiptLongIcon fontSize="small" />
                        </Stack>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(totalApprovedAmount)}</Typography>
                        <Typography variant="caption" color="text.secondary">{entryApprovedCount} entries</Typography>
                    </Paper>
                </Stack>
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
                <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 1.5, cursor: "pointer" }}
                    onClick={() => {
                        setDialogPage(0);
                        setShowDialog(true);
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {activeStatusLabel} entries
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {activeEntryCount} entries, total {formatCurrency(activeTotalAmount)}
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            color={selectedStatus === "approved" ? "info" : "warning"}
                            label={activeStatusLabel}
                            sx={{ borderRadius: 1, textTransform: 'capitalize' }}
                        />
                    </Stack>
                </Paper>

                <Dialog
                    open={showDialog}
                    onClose={() => setShowDialog(false)}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle sx={{ pb: 1.25, pr: 6 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Timesheet entries
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setShowDialog(false)}
                            sx={{ position: "absolute", right: 12, top: 12 }}
                            aria-label="Close timesheet entries dialog"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers sx={{ maxHeight: '70vh' }}>
                        {activeEntries.length === 0 ? (
                            <Alert severity="info">No entries found.</Alert>
                        ) : (
                            <Stack spacing={1.25}>
                                {paginatedEntries.map((entry, index) => (
                                    <Paper key={`${entry.timesheetId || 'entry'}-${dialogPage}-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    {entry.account_name || "N/A"}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {dayjs(entry.date?.toDate ? entry.date.toDate() : entry.date).format("YYYY-MM-DD")}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={(entry.status || 'open').toLowerCase()}
                                                size="small"
                                                color={(entry.status || '').toLowerCase() === "approved" ? "info" : "warning"}
                                                sx={{ borderRadius: 1, textTransform: 'capitalize' }}
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                            Hours: {entry.hours} • Rate: {formatCurrency(entry.rate)} • Amount: {formatCurrency(entry.hours * entry.rate)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                                            {entry.description || "No description provided."}
                                        </Typography>

                                        {selectedStatus === 'open' && (
                                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    disabled={processing}
                                                    startIcon={processing ? <Loader size={14} /> : <DeleteIcon fontSize="small" />}
                                                    sx={{ textTransform: 'none' }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setProcessing(true);
                                                        const workRecord = {
                                                            action: 'delete',
                                                            entry_id: String(entry.entryId || ''),
                                                            date: dayjs(entry.date?.toDate ? entry.date.toDate() : entry.date).format("YYYY-MM-DD"),
                                                            account_id: entry.account_id,
                                                            hours: Number(entry.hours),
                                                            description: String(entry.description || ''),
                                                        };
                                                        handleDeleteEntry(workRecord).finally(() => {
                                                            setProcessing(false);
                                                        });
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Stack>
                                        )}
                                    </Paper>
                                ))}
                            </Stack>
                        )}
                    </DialogContent>
                    {activeEntries.length > 0 && (
                        <DialogActions sx={{ px: 2, pb: 1.5 }}>
                            <TablePagination
                                component="div"
                                count={activeEntries.length}
                                page={dialogPage}
                                onPageChange={(_event, newPage) => setDialogPage(newPage)}
                                rowsPerPage={dialogRowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setDialogRowsPerPage(parseInt(event.target.value, 10));
                                    setDialogPage(0);
                                }}
                                rowsPerPageOptions={[5, 10, 20, 50]}
                                labelRowsPerPage="Entries"
                            />
                        </DialogActions>
                    )}
                </Dialog>
            </Box>

            <BootstrapDialog
                onClose={handleClose}
                open={isOpen}
                fullWidth
                maxWidth="sm"
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(197, 197, 197, 0.7)',
                            opacity: 1
                        },
                        timeout: 100,
                    },
                }}
            >
                <DialogTitle sx={{ m: 0, p: 0, position: 'relative' }} id="work-log-dialog">
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2.5, pr: 7, borderTop: '4px solid', borderColor: 'primary.main' }}>
                        <Avatar variant="rounded" sx={{ width: 42, height: 42, bgcolor: 'rgba(25, 118, 210, 0.08)', color: 'primary.main' }}>
                            <AddCircleIcon fontSize="small" />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.2 }}>
                                Add Work Submission
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Submit work hours for manager approval.
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton
                        aria-label="Close work submission dialog"
                        onClick={handleClose}
                        size="small"
                        sx={{ position: 'absolute', top: 18, right: 18, color: 'text.secondary' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {(loading) ? (
                        <>
                            <DialogContent dividers sx={{ width: '100%', bgcolor: 'grey.50' }}>
                                <Skeleton animation="wave" sx={{ mb: 1 }} height={16} width={400}/>
                                <Skeleton animation="wave" sx={{ mb: 1 }} height={16} width={400}/>
                                <Skeleton animation="wave" sx={{ mb: 1 }} height={48} width={400}/>
                            </DialogContent>
                        </>
                    ):(
                        <>
                                <DialogContent dividers sx={{ width: '100%', bgcolor: 'grey.50' }}>
                                    <Stack spacing={2}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.75, bgcolor: 'background.paper' }}>
                                          <Stack spacing={2}>
                                        <DatePicker
                                            label="Work date"
                                            value={workDate}
                                            disableFuture
                                            shouldDisableDate={shouldDisableDate}
                                            sx={{ width: '100%' }}
                                            slotProps={{
                                                field: { clearable: false },
                                                textField: {
                                                    error: workDate ? false : true,
                                                    size: 'small',
                                                    helperText: 'Work date must be a weekday between last month and today.',
                                                },
                                            }}
                                            onChange={(newValue) => {
                                                setWorkDate(newValue);
                                            }}
                                        />
                                        <TextField
                                            label="Hours worked"
                                            id="outlined-start-adornment"
                                            size="small"
                                            value={units}
                                            sx={{ width: '100%' }}
                                            inputProps={{
                                                inputMode: 'decimal',
                                                pattern: '^[0-8](\\.\\d{1,2})?$',
                                                min: 0,
                                                max: 8,
                                                step: 0.01
                                            }}
                                            helperText="Enter between 0.01 and 8.00 hours."
                                            onChange={handleUnitChange}
                                        />
                                        <Autocomplete
                                            fullWidth
                                            value={(account !== '') ? (accounts.find(obj => obj.id === account)) : null}
                                            options={accounts}
                                            disabled={false}
                                            autoHighlight
                                            size="small"
                                            getOptionLabel={(option) => option.name}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props}>
                                                    {option.name}
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Project"
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        autoComplete: 'new-password',
                                                    }}
                                                />
                                            )}
                                            onChange={(event, newValue) => {
                                                if (newValue && newValue.id) {
                                                    setAccount(newValue.id);
                                                }
                                            }}
                                        />
                                        <TextField
                                            id="outlined-multiline-description"
                                            label="Work description (optional)"
                                            multiline
                                            size="small"
                                            rows={4}
                                            sx={{ width: '100%' }}
                                            value={description}
                                            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
                                            onChange={handleDescriptionChange}
                                            helperText={`${description.length}/${MAX_DESCRIPTION_LENGTH} characters`}
                                        />
                                          </Stack>
                                        </Paper>
                                    </Stack>
                                    <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5, alignItems: 'center' }}>
                                        Hourly rate is automatically applied based on approved pricing effective date.
                                    </Alert>
                                </DialogContent>
                                <DialogActions sx={{ px: 2.5, py: 2, bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
                                    <Button fullWidth variant="contained" disabled={(workDate===null||units===''||account==='')?true:inSubmit} sx={{textTransform: 'none', borderRadius: 1.25, fontWeight: 800, py: 1.15 }} onClick={handleCreateEntry} >
                                        {inSubmit?<Loader size={24} />:"Submit"}
                                    </Button>
                                </DialogActions>
                        </>
                    )}
                </LocalizationProvider>
            </BootstrapDialog> 

            <Snackbar open={error && !!message} autoHideDuration={5000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right'}}>
                <Alert
                    onClose={handleSnackbarClose}
                    severity="error"
                    variant="standard"
                    sx={{ width: '100%' }}
                >
                    {message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default TimesheetSummary;
